import {BaseCommand} from './base.command';
import {MultiTextSnapshot} from '../interfaces/command.interface';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {NodeService} from '../../../api/services/node.service';
import {firstValueFrom, of} from 'rxjs';
import {catchError, defaultIfEmpty} from 'rxjs/operators';
import {UndoLabelMetadata, UndoObjectType} from '../undo-label.utils';

interface MultiTextCommandData {
  changes: MultiTextSnapshot[];
}

/**
 * MultiTextChangeCommand handles text field changes (name/description) for multiple elements.
 * Supports multi-selection text changes as a single atomic operation.
 */
export class MultiTextChangeCommand extends BaseCommand<MultiTextCommandData> {
  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    changes: MultiTextSnapshot[]
  ) {
    const count = changes.length;

    // Determine object type based on count
    const objectType: UndoObjectType = count === 1
      ? (changes[0].entityType === 'node' ? 'Node' : 'Link')
      : 'Selection';

    // Map field to display name
    const field = changes[0].field;
    const partName = field === 'name' ? 'Name' : field === 'description' ? 'Description' : 'Text';

    const labelMetadata: UndoLabelMetadata = {
      object: objectType,
      verb: 'Change',
      part: partName,
      elementCount: objectType === 'Selection' ? count : undefined
    };

    super(
      'text-change',
      `${count}-Element Selection ${partName} Change`,
      {
        before: {changes},
        after: {changes}
      },
      labelMetadata
    );
  }

  async execute(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const {changes} = this.snapshot.after;

    cy.batch(() => {
      changes.forEach(change => {
        const element = cy.$(`#${change.entityId}`);
        if (element.length > 0) {
          element.data(change.field, change.after);
        }
      });
    });

    // Update on backend
    const updatePromises = changes.map(change => {
      const payload = {id: change.entityId, [change.field]: change.after};

      if (change.entityType === 'node') {
        return firstValueFrom(
          this.nodeService.updateNode(payload as any).pipe(
            defaultIfEmpty(null),
            catchError(err => {
              console.warn('MultiTextChangeCommand: Failed to update node', change.entityId, err);
              return of(null);
            })
          )
        );
      } else {
        return firstValueFrom(
          this.nodeService.updateLink(payload as any).pipe(
            defaultIfEmpty(null),
            catchError(err => {
              console.warn('MultiTextChangeCommand: Failed to update link', change.entityId, err);
              return of(null);
            })
          )
        );
      }
    });

    await Promise.all(updatePromises);
    cy.style().update();
  }

  async undo(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const {changes} = this.snapshot.before;

    cy.batch(() => {
      changes.forEach(change => {
        const element = cy.$(`#${change.entityId}`);
        if (element.length > 0) {
          element.data(change.field, change.before);
        }
      });
    });

    // Update on backend
    const updatePromises = changes.map(change => {
      const payload = {id: change.entityId, [change.field]: change.before};

      if (change.entityType === 'node') {
        return firstValueFrom(
          this.nodeService.updateNode(payload as any).pipe(
            defaultIfEmpty(null),
            catchError(err => {
              console.warn('MultiTextChangeCommand: Failed to undo node update', change.entityId, err);
              return of(null);
            })
          )
        );
      } else {
        return firstValueFrom(
          this.nodeService.updateLink(payload as any).pipe(
            defaultIfEmpty(null),
            catchError(err => {
              console.warn('MultiTextChangeCommand: Failed to undo link update', change.entityId, err);
              return of(null);
            })
          )
        );
      }
    });

    await Promise.all(updatePromises);
    cy.style().update();
  }
}
